/**
 * ShownContext - handles state of which cards are shown
 */
import { contextLogger } from '@annote/core/utils/debug';
import React, { useEffect, useState, ReactNode, useContext, useCallback, useMemo } from 'react';
import type { UUID, NoteMap, Source, CardSize, Note } from '@annote/types';
import { isSource, isUUID } from '@annote/types';
import { scrollToNote } from 'src/helpers/scrolling';
import { useBrainContext } from './BrainContext';
import { annoteIndex } from './annoteIndex';
import { noteCache } from './noteCache';
import { FocusContext } from './FocusContext';
import { useNotes } from './noteCacheHooks';

type SizeMap = Record<UUID, CardSize>;
const sizeKeyIndex: Record<string, CardSize> = { S: 'SMALL', M: 'MEDIUM', L: 'LARGE' };

export type ShownIdsContextType = {
  shownIds: UUID[]; // a list of the ids of the notes that are to be shown
  shownNotes: (Note | undefined)[]; // the actual note objects for shown ids
  surfaceNoteMap: NoteMap; // a map of all notes that are shown or needed by shown notes on the surface
  show: (noteId: UUID | UUID[]) => void; // show a note or list of notes
  showAafterB: (a: UUID | UUID[], b?: UUID) => void; // show note a after note b
  showAbeforeB: (a: UUID | UUID[], b?: UUID) => void; // show note a before note b
  hide: (noteId: UUID | UUID[], ignoreSources?: boolean) => void; // hide a note or list of notes
  clear: () => void; // hide all notes
  updateSize: (noteId: UUID, size: CardSize) => void; // function to change the size (SMALL, MEDIUM, LARGE) of a note
  shownSizes: SizeMap; // state for the size (small, medium, large) of each note
};

const defaultShownIdsContext: ShownIdsContextType = {
  shownIds: [],
  shownNotes: [],
  surfaceNoteMap: {},
  show: () => {},
  shownSizes: {},
  updateSize: () => {},
  showAafterB: () => {},
  showAbeforeB: () => {},
  hide: () => {},
  clear: () => {},
};

// Unfortunately this is necessary for the comparison of two lists of ids
const areArraysEqual = (a: UUID[], b: UUID[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const ShownIdsContext = React.createContext<ShownIdsContextType>(defaultShownIdsContext);

type ShownIdsProviderProps = {
  children: ReactNode;
};

export const ShownIdsProvider: React.FC<ShownIdsProviderProps> = ({ children }) => {
  const [shownIds, setShownIds] = useState<UUID[]>([]);
  const [shownSizes, setShownSizes] = useState<SizeMap>({});
  const [urlSource, setUrlSource] = useState<Source | undefined | null>(undefined);
  const [surfaceIds, setSurfaceIds] = useState<UUID[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const { getSourceByUrl, getNotes } = useBrainContext();
  const { setFocus } = useContext(FocusContext);

  // Internal note management with hooks
  const shownNotes = useNotes(shownIds);
  const surfaceNotes = useNotes(surfaceIds);

  // Convert to note maps for external consumption
  const surfaceNoteMap: NoteMap = React.useMemo(() => {
    const map: NoteMap = {};
    surfaceNotes.forEach((note) => {
      if (note) map[note.id] = note;
    });
    return map;
  }, [surfaceNotes]);

  const urlSourceRef = React.useRef<Source | undefined | null>(undefined);

  useEffect(() => {
    contextLogger.debug('shown ids updated', shownIds);
  }, [shownIds]);

  useEffect(() => {
    contextLogger.debug('surfaceIds changed', surfaceIds);
  }, [surfaceIds]);

  // save urlSource as a ref as it lowers dependency issues
  useEffect(() => {
    urlSourceRef.current = urlSource;
  }, [urlSource]);

  // Store mutable values in refs so callbacks can read the latest state
  // without needing to be re-created on every render.
  const stateRef = React.useRef<{
    hide: (noteId: UUID | UUID[], ignoreSources?: boolean) => void;
    shownIds: UUID[];
    surfaceIds: UUID[];
  }>({
    hide: (() => {}) as (noteId: UUID | UUID[], ignoreSources?: boolean) => void,
    shownIds,
    surfaceIds,
  });

  const surfaceNoteMapRef = React.useRef<NoteMap>(surfaceNoteMap);
  useEffect(() => {
    surfaceNoteMapRef.current = surfaceNoteMap;
  }, [surfaceNoteMap]);

  // function to change the size (small, medium, large) of a note
  const updateSize = useCallback((noteId: UUID, size: CardSize): void => {
    if (noteId === urlSourceRef.current?.id) {
      setUrlSource(null); // remove the ?url= get param if its size was updated
    }
    setShownSizes((currentShownSizes) => ({ ...currentShownSizes, [noteId]: size }));
  }, []);

  // adds the noteId (or list of noteIds) to the front of shownIds
  const show = useCallback((noteId: UUID | UUID[]): void => {
    const idsToShow = Array.isArray(noteId) ? noteId : [noteId];
    setShownIds((currentShownIds) => {
      const cleanIds = currentShownIds.filter((id) => !idsToShow.includes(id));
      return [...idsToShow, ...cleanIds];
    });
  }, []);

  const showAafterB = useCallback((a: UUID | UUID[], b?: UUID): void => {
    if (!b) {
      show(a);
      return;
    }
    const idsToAdd = Array.isArray(a) ? a : [a];
    if (idsToAdd.length === 1 && idsToAdd[0] === b) return; // same spot - don't move
    setShownIds((currentShownIds) => {
      const shownIdsWithoutA = currentShownIds.filter((id) => !idsToAdd.includes(id)); // Note B must exist
      const indexOfB = shownIdsWithoutA.indexOf(b);
      const beforeB = shownIdsWithoutA.slice(0, indexOfB + 1);
      const afterB = shownIdsWithoutA.slice(indexOfB + 1);
      return [...beforeB, ...idsToAdd, ...afterB];
    });
  }, []);

  const showAbeforeB = useCallback((a: UUID | UUID[], b?: UUID): void => {
    if (!b) {
      show(a);
      return;
    }
    const idsToAdd = Array.isArray(a) ? a : [a];
    setShownIds((currentShownIds) => {
      const shownIdsWithoutA = currentShownIds.filter((id) => !idsToAdd.includes(id)); // Note B must exist
      const indexOfB = shownIdsWithoutA.indexOf(b);
      const beforeB = shownIdsWithoutA.slice(0, indexOfB);
      const afterB = shownIdsWithoutA.slice(indexOfB);
      return [...beforeB, ...idsToAdd, ...afterB];
    });
  }, []);

  const clear = useCallback((): void => {
    setShownIds([]);
    setShownSizes({});
    setUrlSource(null);
  }, []);

  // Needs to be a callback because it's passed as context to other components
  const hide = useCallback(
    (noteId: UUID | UUID[], ignoreSources = false): void => {
      const idsToRemove: UUID[] = Array.isArray(noteId) ? noteId : [noteId];

      if (!ignoreSources) {
        // Read the latest surfaceNoteMap and shownIds from refs so `hide`
        // can remain stable across renders.
        const notesToRemove = idsToRemove.map((id) => surfaceNoteMapRef.current[id]).filter((n) => n);
        notesToRemove.forEach((n) => {
          if (isSource(n)) {
            // if the source to be hidden matches the reader url, clear the reader url
            if (urlSourceRef.current && n.id === urlSourceRef.current?.id) setUrlSource(null);

            // Remove shown annotes of source we're hiding using annoteIndex
            const annoteIds = annoteIndex.getAnnoteIds(n.links);
            const shownAnnoteIds = annoteIds.filter((id) => stateRef.current.shownIds.includes(id));
            idsToRemove.push(...shownAnnoteIds);
          }
        });
      }

      setShownIds((currentShownIds) => {
        const filteredIds = currentShownIds.filter((id) => !idsToRemove.includes(id));
        return filteredIds;
      });
    },
    // annoteIndex may change if the brain context changes; include it.
    [annoteIndex],
  );

  // workaround to ensure these callbacks don't trigger extra updates
  useEffect(() => {
    stateRef.current = {
      hide,
      shownIds,
      surfaceIds,
    };
  }, [hide, shownIds, surfaceIds]);

  // Set the initial shownIds and sizes from the URL after the component mounts
  useEffect(() => {
    if (isInitialized) return;
    const params = new URLSearchParams(window.location.search);
    const docsParam = params.get('docs') || '';
    const idsAndSizes = docsParam.split(',').filter((id) => id !== '');

    const sizes: SizeMap = {};
    const ids: UUID[] = [];
    const ignoredEntries: string[] = [];

    idsAndSizes.forEach((idAndSize) => {
      const parts = idAndSize.split(':');
      const id = parts[0];
      // Only accept valid UUIDs to avoid accidental titles (e.g., NoteTitle1)
      if (!isUUID(id)) {
        ignoredEntries.push(id);
        return;
      }

      ids.push(id);
      if (parts.length > 1) {
        // Only add to sizes if there's actually a size specified
        const size = parts[1].toUpperCase();
        if (size in sizeKeyIndex) {
          sizes[id] = sizeKeyIndex[size] as CardSize;
        }
      }
    });

    if (ignoredEntries.length > 0) {
      // Remove invalid entries from the URL so downstream code won't try to fetch them
      const cleanedDocs = ids
        .map((id) => {
          if (sizes[id]) return `${id}:${sizes[id][0]}`;
          return id;
        })
        .join(',');

      if (cleanedDocs.length > 0) {
        params.set('docs', cleanedDocs);
      } else {
        params.delete('docs');
      }

      let paramsString = `?${params.toString()}`;
      if (paramsString.length < 2) paramsString = '';
      window.history.replaceState({}, '', `${window.location.pathname}${paramsString}`);

      contextLogger.info('Removed invalid docs entries from URL:', ignoredEntries);
    }

    // show a large source from the url parm if it is there
    const url = params.get('url') || null;
    if (url) {
      getSourceByUrl(url).then((source) => {
        if (!source) {
          setUrlSource(null);
          return;
        }
        setUrlSource(source);
        if (!ids.includes(source.id)) {
          show(source.id);
        }
        setShownSizes((currentShownSizes) => ({ ...currentShownSizes, [source.id]: 'LARGE' }));
      });
    }

    setShownSizes(sizes);
    setIsInitialized(true);
    show(ids);
  }, []);

  // Update the Window Location whenever shownIds, sizes, or readerUrl change
  useEffect(() => {
    if (!isInitialized) return;
    const params = new URLSearchParams(window.location.search);
    if (urlSource === null) {
      params.delete('url');
    }
    params.delete('docs');

    // put the sizes with the ids in the following format: id:S,id:L
    const idsWithSizes = shownIds
      .map((id) => {
        if (!shownSizes[id]) return id;
        return `${id}:${shownSizes[id][0]}`;
      })
      .join(',');
    if (idsWithSizes.length > 0) params.set('docs', idsWithSizes);
    let paramsString = `?${params.toString()}`;
    if (paramsString.length < 2) paramsString = ''; // remove the ? if there are no params

    window.history.replaceState({}, '', `${window.location.pathname}${paramsString}`);
  }, [shownIds, shownSizes, urlSource]);

  // Ensure deleted notes are hidden
  useEffect(() => {
    const handleCacheChange = (changedIds: string[]): void => {
      // Check for notes that no longer exist in cache (deleted notes)
      const deletedIds = changedIds.filter((id) => {
        return !noteCache.has(id);
      });

      if (deletedIds.length > 0) {
        hide(deletedIds);
      }
    };

    const unsubscribe = noteCache.subscribe(handleCacheChange);
    return unsubscribe;
  }, [noteCache, hide]);

  // shownIds ==> surfaceIds (optimized with annoteIndex)
  // populate surfaceIds -> a list of ids that the shownNotes depend on (their connections)
  // these are all the notes that are needed to show the surface and will be hydrated in surfaceNoteMap
  useEffect(() => {
    // Compute surface IDs using annoteIndex for skip-over expansion
    const computedSurfaceIds = Array.from(
      new Set(
        [
          ...shownIds,
          ...shownNotes.flatMap((n) =>
            n
              ? [
                  ...annoteIndex.expandSkipOvers(n.links || [], 'links'),
                  ...annoteIndex.expandSkipOvers(n.backlinks || [], 'backlinks'),
                ]
              : [],
          ),
        ]
          .filter((id) => isUUID(id))
          .sort(),
      ),
    );

    // don't refresh if the ids are the same
    if (areArraysEqual(computedSurfaceIds, surfaceIds)) return;
    setSurfaceIds(computedSurfaceIds);

    // Ensure notes for the surface are loaded into the cache so downstream
    // subscribers (e.g. FlowSurface's useNotes(shownIds)) receive the data.
    // Fire-and-forget; getNotes will upsert into noteCache which triggers
    // subscriptions. We intentionally ignore the promise result.
    getNotes(computedSurfaceIds).catch(() => undefined);
  }, [shownIds, shownNotes, annoteIndex]);

  // Handle when links in notes are clicked
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;

      // uncomment if you want to block external links too
      // if (!target.matches('a')) return;
      const href = target.getAttribute('href');
      if (!href) return;
      // Focus on the internal link
      if (!target.classList.contains('internal-link')) return;

      e.preventDefault();
      e.stopPropagation();
      const noteId = href;
      setFocus(noteId);
      if (!stateRef.current.shownIds.includes(noteId)) show(noteId);
      // TODO probably a better listener for this than a timeout
      setTimeout(() => scrollToNote(noteId), 100);
    };
    document.addEventListener('click', handleLinkClick);
    return () => document.removeEventListener('click', handleLinkClick);
  }, [show]);

  // Listener for sources being fetched from the extension
  // Extension can call showAbeforeB, show, and showAafterB
  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.source !== window) return;
      if (event.data.action === 'showAbeforeB') {
        const response = event.data;
        showAbeforeB(response.a, response.b);
      }
      if (event.data.messageType === 'EXT_REQUEST' && event.data.message) {
        const response = event.data.message;
        const { action } = response;
        if (action === 'show') {
          if (response.ids.length === 0) return;
          show(response.ids);

          setTimeout(() => {
            show(response.ids);
            setFocus(response.ids[0]);
          }, 100);
        }
      }
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      shownIds,
      shownNotes,
      show,
      showAafterB,
      showAbeforeB,
      hide,
      clear,
      updateSize,
      shownSizes,
      surfaceNoteMap,
    }),
    [shownIds, shownNotes, show, showAafterB, showAbeforeB, hide, clear, updateSize, shownSizes, surfaceNoteMap],
  );

  return <ShownIdsContext.Provider value={contextValue}>{children}</ShownIdsContext.Provider>;
};
